import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../Context/ThemeContext';
import { AccountDeletionHandler, DeletionRequest, DeletionResponse } from './AccountDeletionHandler';

const getStatusLabel = (status: number | undefined): string => {
    switch (status) {
        case 0: return 'Pending';
        case 1: return 'Canceled';
        case 2: return 'Canceled By User';
        case 3: return 'Rushed';
        case 4: return 'Deleted';
        default: return 'Unknown';
    }
};

const getStatusColor = (status: number | undefined): string => {
    switch (status) {
        case 0: return '#f59e0b';
        case 1: return '#6b7280';
        case 2: return '#6b7280';
        case 3: return '#8b5cf6';
        case 4: return '#ef4444';
        default: return '#6b7280';
    }
};

const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const AccountDeletionPage: React.FC = () => {
    const { theme, isMobile } = useTheme();

    const [requests, setRequests] = useState<DeletionRequest[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const pageSize = 10;

    const fetchData = useCallback(async (page: number) => {
        setLoading(true);
        setError(null);
        try {
            const handler = new AccountDeletionHandler();
            const response: DeletionResponse = await handler.getDeletionRequests(null, page, pageSize);
            setRequests(response.requests || []);
            setTotalCount(response.count || 0);
        } catch (err) {
            console.error('Error fetching deletion requests:', err);
            setError('Failed to load deletion requests.');
            setRequests([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData(currentPage);
    }, [fetchData, currentPage]);

    const totalPages = Math.ceil(totalCount / pageSize);

    const styles: Record<string, React.CSSProperties> = {
        container: {
            padding: 0,
        },
        title: {
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: 700,
            color: theme.titleColor,
            marginBottom: '8px',
        },
        subtitle: {
            fontSize: '14px',
            color: theme.textSecondary,
            marginBottom: '24px',
        },
        tableContainer: {
            background: theme.card,
            borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
        tableWrapper: {
            overflowX: 'auto' as const,
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            tableLayout: 'fixed' as const,
            minWidth: isMobile ? '700px' : 'auto',
        },
        th: {
            padding: '14px 16px',
            textAlign: 'center' as const,
            fontWeight: 600,
            fontSize: '13px',
            color: theme.textSecondary,
            background: theme.tableHeader,
            borderBottom: `1px solid ${theme.border}`,
            userSelect: 'none' as const,
            whiteSpace: 'nowrap' as const,
        },
        td: {
            padding: '14px 16px',
            textAlign: 'center' as const,
            fontSize: '14px',
            color: theme.text,
            borderBottom: `1px solid ${theme.border}`,
            whiteSpace: 'nowrap' as const,
        },
        badge: {
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600,
        },
        pagination: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderTop: `1px solid ${theme.border}`,
            flexWrap: 'wrap' as const,
            gap: '12px',
        },
        paginationInfo: {
            fontSize: '14px',
            color: theme.textSecondary,
        },
        paginationButtons: {
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
        },
        pageButton: {
            padding: '8px 14px',
            fontSize: '13px',
            fontWeight: 500,
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            cursor: 'pointer',
        },
        pageButtonDisabled: {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
        loadingContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px 0',
        },
        spinner: {
            width: '24px',
            height: '24px',
            border: '2px solid rgba(59, 130, 246, 0.3)',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
        errorBanner: {
            padding: '12px 16px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#ef4444',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        emptyRow: {
            textAlign: 'center' as const,
            padding: '40px',
            color: theme.textSecondary,
            fontStyle: 'italic' as const,
        },
    };

    return (
        <div style={styles.container}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

            <h1 style={styles.title}>Account Deletion Log</h1>
            <p style={styles.subtitle}>Log of player account deletions</p>

            {error && (
                <div style={styles.errorBanner}>
                    <span>{error}</span>
                    <button
                        onClick={() => fetchData(currentPage)}
                        style={{ padding: '4px 12px', fontSize: '13px', fontWeight: 600, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Data Table */}
            <div style={styles.tableContainer}>
                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Username</th>
                                <th style={styles.th}>Email</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Deletion Date</th>
                                <th style={styles.th}>Record Saved</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={5} style={styles.td}>
                                        <div style={styles.loadingContainer}>
                                            <div style={styles.spinner} />
                                        </div>
                                    </td>
                                </tr>
                            ) : requests.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ ...styles.td, ...styles.emptyRow }}>
                                        No deletion requests found
                                    </td>
                                </tr>
                            ) : (
                                requests.map((req) => {
                                    const statusColor = getStatusColor(req.deletionStatus);
                                    return (
                                        <tr
                                            key={req.id}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = theme.tableRowHover; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                        >
                                            <td style={{ ...styles.td, fontWeight: 500 }}>{req.usernameAtDeletion || '-'}</td>
                                            <td style={styles.td}>{req.emailAtDeletion || '-'}</td>
                                            <td style={styles.td}>
                                                <span style={{
                                                    ...styles.badge,
                                                    background: `${statusColor}22`,
                                                    color: statusColor,
                                                }}>
                                                    {getStatusLabel(req.deletionStatus)}
                                                </span>
                                            </td>
                                            <td style={styles.td}>{formatDate(req.scheduledDeletionDate)}</td>
                                            <td style={styles.td}>{formatDate(req.creationDate)}</td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalCount > 0 && (
                    <div style={styles.pagination}>
                        <span style={styles.paginationInfo}>
                            Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount} requests
                        </span>
                        <div style={styles.paginationButtons}>
                            <button
                                style={{ ...styles.pageButton, ...(currentPage === 1 ? styles.pageButtonDisabled : {}) }}
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                            >
                                First
                            </button>
                            <button
                                style={{ ...styles.pageButton, ...(currentPage === 1 ? styles.pageButtonDisabled : {}) }}
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span style={{ padding: '8px 14px', fontSize: '13px', color: theme.text }}>
                                Page {currentPage} of {totalPages || 1}
                            </span>
                            <button
                                style={{ ...styles.pageButton, ...(currentPage >= totalPages ? styles.pageButtonDisabled : {}) }}
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage >= totalPages}
                            >
                                Next
                            </button>
                            <button
                                style={{ ...styles.pageButton, ...(currentPage >= totalPages ? styles.pageButtonDisabled : {}) }}
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage >= totalPages}
                            >
                                Last
                            </button>
                        </div>
                    </div>
                )}
            </div>

        </div>
    );
};

export default AccountDeletionPage;
